import React from 'react';
import type { Ingredient } from '../types';
import CookingQuantityCapsule from './CookingQuantityCapsule';

const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);

interface CookingIngredientRowProps {
    ingredient: Ingredient;
    isStruck: boolean;
    isSelectionMode: boolean;
    isSelected: boolean;
    sourcesLabel?: string;
    t: (key: any) => string;
    rtl: boolean;
    onToggle: () => void;
}

const CookingIngredientRow: React.FC<CookingIngredientRowProps> = ({
    ingredient: ing,
    isStruck,
    isSelectionMode,
    isSelected,
    sourcesLabel,
    t,
    rtl,
    onToggle,
}) => {
    let rowClass = 'flex items-start gap-3 p-4 transition-all cursor-pointer ';
    if (ing.isMeat) rowClass += 'bg-rose-50/40 dark:bg-rose-900/20 border-l-4 border-l-rose-500 dark:border-l-rose-600 ';
    else rowClass += 'hover:bg-slate-50/50 dark:hover:bg-slate-800/50 ';

    return (
        <div className={rowClass} onClick={onToggle}>
            <div
                className={`flex items-center justify-center transition-all duration-300 flex-shrink-0 mt-0.5 ${
                    isSelectionMode
                        ? `w-6 h-6 rounded border-2 ${isSelected ? 'bg-brand-orange border-brand-orange text-white' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'}`
                        : `w-6 h-6 rounded-full border-2 ${isStruck ? 'bg-brand-sage border-brand-sage text-white' : 'border-stone-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-inner'}`
                }`}
            >
                {isSelectionMode ? (isSelected && <CheckIcon className="w-4 h-4" />) : (isStruck && <CheckIcon className="w-3.5 h-3.5" />)}
            </div>
            <div className="flex-1 min-w-0">
                <span
                    className={`text-lg font-bold tracking-tight transition-all break-words leading-snug ${
                        !isSelectionMode && isStruck
                            ? 'line-through text-slate-300 dark:text-slate-600'
                            : ing.isMeat
                                ? 'text-rose-900 dark:text-rose-200'
                                : 'text-slate-700 dark:text-slate-200'
                    }`}
                >
                    {ing.name}
                </span>
                {sourcesLabel && (
                    <span className="block mt-0.5 text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-tighter break-words">{sourcesLabel}</span>
                )}
                {ing.isMeat && !sourcesLabel && (
                    <span className={`block mt-0.5 text-[9px] font-black uppercase tracking-widest ${isStruck && !isSelectionMode ? 'text-slate-300 dark:text-slate-600' : 'text-rose-400 dark:text-rose-400'}`}>
                        {t('butcherItem')}
                    </span>
                )}
                {ing.alternatives && ing.alternatives.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                        {ing.alternatives.map((alt, altIdx) => (
                            <p
                                key={altIdx}
                                className={`text-xs font-medium ${isStruck && !isSelectionMode ? 'text-slate-300 dark:text-slate-600 line-through' : 'text-slate-500 dark:text-slate-400'}`}
                            >
                                {alt.name} — <CookingQuantityCapsule quantity={alt.quantity} unit={alt.unit} t={t} rtl={rtl} variant="inline" struck={isStruck && !isSelectionMode} />
                            </p>
                        ))}
                    </div>
                )}
            </div>
            <CookingQuantityCapsule
                quantity={ing.quantity}
                unit={ing.unit}
                t={t}
                rtl={rtl}
                isMeat={ing.isMeat}
                struck={isStruck && !isSelectionMode}
            />
        </div>
    );
};

export default CookingIngredientRow;
